import React, { useState } from 'react';

import { css } from '@emotion/css';
import { Card, CardContent, Theme } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { PageContainer } from '@/pages/PageContainer.tsx';
import { PatientListCardContent } from '@/pages/patients/patients-list/PatientListCard.tsx';
import { Queries } from '@/queries/Queries.ts';
import { ScreenMax, ScreenMin } from '@/theme/responsive.ts';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { PatientCreation } from '@/uiKit/organisms/PatientCreationAndRenewal/PatientCreation.tsx';

export const PatientsPage: React.FC = () => {
  const { t } = useTranslation();
  const [openCreatePatient, setOpenCreatePatient] = useState<boolean>(false);
  const navigate = useNavigate();
  const me = Queries.user.usePractitionerMe({});
  const isDoctor = me.qualification === 'doctor';

  const styles = useStyles(makeStyles);

  return (
    <PageContainer
      id="patients-list-page"
      title={t('pages.patients.title')}
      content={
        isDoctor && (
          <Button
            id="create-patient-button-sm"
            className={styles.createPatientSM}
            variant="contained"
            startIcon={<Icons.userPlus />}
            onClick={() => setOpenCreatePatient(true)}
          >
            {t('pages.patients.addPatient')}
          </Button>
        )
      }
      className={styles.container}
    >
      <Card elevation={0}>
        <CardContent className={styles.content}>
          <PatientListCardContent me={me} />
          {isDoctor && (
            <Button
              id="create-patient-button"
              className={styles.createPatient}
              variant="contained"
              startIcon={<Icons.userPlus />}
              onClick={() => setOpenCreatePatient(true)}
            >
              {t('pages.patients.addPatient')}
            </Button>
          )}
        </CardContent>
      </Card>
      {isDoctor && (
        <PatientCreation
          open={openCreatePatient}
          onClose={() => setOpenCreatePatient(false)}
          onSubmit={(patientId: string) =>
            navigate({
              to: `/patients/${patientId}`,
            })
          }
        />
      )}
    </PageContainer>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(12)};

    padding: 2vh 2vw;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing(12, 16)};
  `,
  createPatient: css`
    width: fit-content;
    align-self: flex-end;
    position: sticky;
    margin: ${theme.spacing(12, 0, 0, 0)};
    bottom: ${theme.spacing(12)};

    ${ScreenMax.SM} {
      display: none;
    }
  `,
  createPatientSM: css`
    width: fit-content;
    justify-self: flex-end;

    ${ScreenMin.SM} {
      display: none;
    }
  `,
});
